import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/pt-br";

dayjs.extend(customParseFormat);

const DEFAULT_Y_STEP = 5;
const DEFAULT_Y_MAX = 20;

const getAxisStep = (min: number, max: number) => {
  const range = max - min;

  if (range <= 20) return 5;
  if (range <= 50) return 10;
  if (range <= 100) return 20;
  if (range <= 500) return 50;
  if (range <= 1_000) return 100;
  if (range <= 5_000) return 500;

  return 1_000;
};

const roundAxisBound = (value: number, step: number, mode: "floor" | "ceil") =>
  mode === "floor" ? Math.floor(value / step) * step : Math.ceil(value / step) * step;

const buildYAxisTicks = (min: number, max: number, step: number) => {
  if (min > max) return [0];

  const ticks: number[] = [];
  for (let tick = min; tick <= max; tick += step) ticks.push(tick);
  return ticks;
};

export const computeChartYAxisDomain = (values: number[]) => {
  const filtered = values.filter((value) => Number.isFinite(value));

  if (filtered.length === 0) {
    return { min: 0, max: DEFAULT_Y_MAX, ticks: buildYAxisTicks(0, DEFAULT_Y_MAX, DEFAULT_Y_STEP) };
  }

  const dataMin = Math.min(...filtered);
  const dataMax = Math.max(...filtered);

  if (dataMin === 0 && dataMax === 0) {
    return { min: 0, max: DEFAULT_Y_MAX, ticks: buildYAxisTicks(0, DEFAULT_Y_MAX, DEFAULT_Y_STEP) };
  }

  const provisionalMin = dataMin < 0 ? dataMin : 0;
  const provisionalMax = dataMax > 0 ? dataMax : 0;
  const step = getAxisStep(provisionalMin, provisionalMax || DEFAULT_Y_MAX);

  const min = dataMin < 0 ? roundAxisBound(dataMin, step, "floor") : 0;
  const max = dataMax > 0 ? roundAxisBound(Math.max(dataMax, step), step, "ceil") : min < 0 ? 0 : DEFAULT_Y_MAX;

  return { min, max, ticks: buildYAxisTicks(min, max, step) };
};

export const formatChartNumberTick = (value: number) => new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(value);

export const formatMonthAxisTick = (month: string) => {
  const label = dayjs(month, "YYYY-MM").locale("pt-br").format("MMM/YY");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const getChartYAxisWidth = (ticks: number[]) => {
  const longestTick = ticks.reduce((longest, tick) => {
    const label = formatChartNumberTick(tick);
    return label.length > longest.length ? label : longest;
  }, "0");

  return Math.max(32, longestTick.length * 7 + 12);
};
